import { Crown, Shield, ShieldOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type AdminRoleRow, labelFor } from "./types";

type Props = {
  row: AdminRoleRow;
  isSelf: boolean;
  isLastSuper: boolean;
  isLastAdmin: boolean;
  isSuperAdmin: boolean;
  busy: boolean;
  superCount: number;
  onPromote: () => void;
  onDemote: () => void;
  onRevoke: () => void;
};

/** Single row in the admin roster, with promote/demote/revoke controls. */
export const AdminRosterRow = ({
  row,
  isSelf,
  isLastSuper,
  isLastAdmin,
  isSuperAdmin,
  busy,
  superCount,
  onPromote,
  onDemote,
  onRevoke,
}: Props) => {
  const label = labelFor(row);
  const promoteDisabled = !isSuperAdmin || busy;
  const demoteDisabled = !isSuperAdmin || busy || isSelf || isLastSuper;
  const revokeDisabled =
    !isSuperAdmin || busy || isSelf || isLastAdmin || (row.is_super && superCount === 1);

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          Granted {row.granted_at ? new Date(row.granted_at).toLocaleDateString() : "—"}
          {isSelf && " · you"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {row.is_super ? (
          <Badge className="gap-1"><Crown className="h-3 w-3" /> Super admin</Badge>
        ) : (
          <Badge variant="secondary" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>
        )}

        {isSuperAdmin && !row.is_super && (
          <Button size="sm" variant="outline" disabled={promoteDisabled} onClick={onPromote}>
            <Crown className="h-4 w-4" /> Promote
          </Button>
        )}
        {isSuperAdmin && row.is_super && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button size="sm" variant="outline" disabled={demoteDisabled} onClick={onDemote}>
                  <ShieldOff className="h-4 w-4" /> Demote
                </Button>
              </span>
            </TooltipTrigger>
            {(isSelf || isLastSuper) && (
              <TooltipContent>
                {isSelf ? "You can't demote yourself" : "Can't demote the last super admin"}
              </TooltipContent>
            )}
          </Tooltip>
        )}
        {isSuperAdmin && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={revokeDisabled}
                  onClick={onRevoke}
                >
                  Revoke
                </Button>
              </span>
            </TooltipTrigger>
            {(isSelf || isLastAdmin || (row.is_super && superCount === 1)) && (
              <TooltipContent>
                {isSelf
                  ? "You can't revoke yourself"
                  : isLastAdmin
                    ? "Can't revoke the last admin"
                    : "Can't revoke the last super admin"}
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </div>
    </div>
  );
};
